import { useSnackbar } from '@hue/design-system';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { getApiError, preferencesApi, type SessionUser, type UserAppearance } from '../lib/api.js';
import { sessionQuery } from '../lib/session.js';
import { useCompanyScope } from './company-context.js';

export function useAppointmentLayout() {
  const { user } = useCompanyScope();
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<{ userId: string; layout: 'columns' | 'stacked' }>();
  const layout =
    (pending && pending.userId === user.id ? pending.layout : undefined) ??
    user.appearance?.appointmentLayout ??
    'columns';

  async function choose(appointmentLayout: 'columns' | 'stacked') {
    setSaving(true);
    const previous = pending;
    setPending({ userId: user.id, layout: appointmentLayout });
    const root = document.documentElement.dataset;
    const appearance: UserAppearance = {
      mode: user.appearance?.mode ?? (root.theme === 'light' ? 'light' : 'dark'),
      palette: user.appearance?.palette ?? ((root.palette as UserAppearance['palette']) || 'hue'),
      density: user.appearance?.density ?? (root.density === 'compact' ? 'compact' : 'comfortable'),
      appointmentLayout,
    };
    try {
      await preferencesApi.updateUserAppearance(appearance);
      queryClient.setQueryData(sessionQuery.queryKey, (current: SessionUser | null | undefined) =>
        current && current.id === user.id
          ? { ...current, appearance: { ...appearance, ...current.appearance, appointmentLayout } }
          : current,
      );
    } catch (error) {
      setPending(previous);
      snackbar.failure(
        `Não foi possível salvar a organização do atendimento. ${getApiError(error)}`,
      );
    } finally {
      setSaving(false);
    }
  }

  return { layout, choose, saving };
}
